import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PostContent from '../components/PostContent';
import RecommendedSection from '../components/RecommendedSection';
import { postsAPI } from '../api';
import { Calendar, ArrowLeft, Pencil, Share2, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const PostDetail = () => {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPost = useCallback(async () => {
    try {
      const response = await postsAPI.getBySlug(slug);
      setPost(response.data);
      // Track view
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/posts/${response.data.id}/view`, { method: 'POST' }).catch(() => {});
    } catch (error) {
      console.error('Error loading post:', error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/news">
            <Button className="bg-cyan-500 hover:bg-cyan-600">
              <ArrowLeft className="mr-2" size={18} />
              Back to News
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {post && (
        <SEO 
          title={post.meta_title || post.title}
          description={post.meta_description || post.excerpt}
          keywords={`Paisley, ${post.category}, ${post.title}`}
          image={post.featured_image}
          url={`/news/${post.slug}`}
          type="article"
          publishedTime={post.created_at}
          modifiedTime={post.updated_at || post.created_at}
          category={post.category}
          author={post.author || "Paisley.org.uk"}
        />
      )}
      <Header />
      
      {/* Hero Image */}
      <div className="relative h-[500px] bg-slate-900">
        <img
          src={post.featured_image}
          alt={post.featured_image_alt || post.title}
          className="w-full h-full object-cover opacity-80"
          onError={(e) => { e.target.src = '/api/files/paisley-org/wp-content/uploads/2025/09/Paisley-skyline-from-abbey-770x513-1.jpg'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
      </div>
      {post.featured_image_caption && (
        <div className="max-w-3xl mx-auto px-6 pt-3">
          <p className="text-xs text-slate-500" data-testid="hero-image-caption">
            {post.featured_image_caption}
            {post.featured_image_credit_url && (
              <>
                {' '}
                <a
                  href={post.featured_image_credit_url}
                  target="_blank"
                  rel="noopener noreferrer license"
                  className="underline hover:text-slate-700"
                >
                  Source
                </a>
              </>
            )}
          </p>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 -mt-32 relative z-10">
        <article className="bg-white rounded-2xl shadow-2xl p-12">
          {/* Meta */}
          <div className="flex items-center gap-4 mb-6 text-sm text-gray-600">
            <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full font-medium">
              {post.category}
            </span>
            <div className="flex items-center gap-2">
              <Calendar size={16} />
              <span>{new Date(post.created_at).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</span>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Excerpt */}
          <p className="text-xl text-gray-700 mb-8 leading-relaxed border-l-4 border-cyan-500 pl-6 italic">
            {post.excerpt}
          </p>

          {/* Content */}
          <PostContent content={post.content} fallbackAlt={post.title} />

          {/* Social Sharing */}
          <div className="mt-10 pt-6 border-t border-gray-100">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-gray-500 font-medium flex items-center gap-1.5"><Share2 size={14} /> Share:</span>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(`https://paisley.org.uk/news/${post.slug}`)}`}
                target="_blank" rel="nofollow noopener noreferrer"
                className="px-4 py-2 bg-[#1DA1F2]/10 text-[#1DA1F2] hover:bg-[#1DA1F2] hover:text-white rounded-lg text-sm font-medium transition-colors"
                data-testid="share-twitter"
              >
                Twitter
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://paisley.org.uk/news/${post.slug}`)}`}
                target="_blank" rel="nofollow noopener noreferrer"
                className="px-4 py-2 bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2] hover:text-white rounded-lg text-sm font-medium transition-colors"
                data-testid="share-facebook"
              >
                Facebook
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${post.title} https://paisley.org.uk/news/${post.slug}`)}`}
                target="_blank" rel="nofollow noopener noreferrer"
                className="px-4 py-2 bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-lg text-sm font-medium transition-colors"
                data-testid="share-whatsapp"
              >
                WhatsApp
              </a>
            </div>
          </div>

          {/* Back Link */}
          <div className="mt-12 pt-8 border-t border-gray-200 flex items-center justify-between">
            <Link to="/news">
              <Button variant="outline" className="border-cyan-500 text-cyan-600 hover:bg-cyan-50">
                <ArrowLeft className="mr-2" size={18} />
                Back to News
              </Button>
            </Link>
            {isAuthenticated && (
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    if (!window.confirm('Move this post to draft? It will be unpublished.')) return;
                    try {
                      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/posts/${post.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
                        body: JSON.stringify({ published: false }),
                      });
                      window.location.reload();
                    } catch (e) { console.error(e); }
                  }}
                  className="inline-flex items-center px-4 py-2 bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-lg text-sm font-medium transition-colors"
                  data-testid="draft-post-btn"
                >
                  <EyeOff className="mr-2" size={16} />
                  Move to Draft
                </button>
                <Link to={`/admin/posts/edit/${post.id}`} data-testid="edit-post-btn">
                  <Button className="bg-slate-800 hover:bg-slate-700 text-white">
                    <Pencil className="mr-2" size={16} />
                    Edit this post
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </article>
      </div>

      {/* Single affiliate slot at the bottom — hides silently if the admin
          has no products with an image (per Brian: no image = no link). */}
      <RecommendedSection
        single
        requireImage
        eyebrow="You might also like"
      />

      <div className="h-24"></div>
      <Footer />
    </div>
  );
};

export default PostDetail;
